/* eslint formatjs/no-literal-string-in-jsx: "error" */

import React from "react"
import { Checkbox, CheckboxGroup, FlexContainer, sizing, Stack } from "@carrotfertility/carotene"
import { useHistory } from "react-router-dom"
import { getProviderFinderSearchURL, ProviderFinderParam } from "../../utils/providerFinderSearchUrl"
import { useUserContext } from "../../context/UserContext"
import {
  getAvailableParentIdsByBenefitConfig,
  PARENT_ID_MAPPING,
  PARENT_ID_TO_PROVIDER_COUNT_MAPPING
} from "../../utils/parentIdMapping"
import { Benefit } from "pages/provider-finder/types/Benefit"
import { useIntl } from "react-intl"
import { useIntlSort } from "../../../../utils/hooks/useIntlSort"
import { useGetHasProviderTypeInCountry } from "pages/provider-finder/hooks/useGetHasProviderTypeInCountry"
import { useCompanyContext } from "pages/provider-finder/context/CompanyContextProvider"

export function ProviderTypeFilter({ benefit }: { benefit: Benefit }): JSX.Element {
  const { companyCountryCode } = useUserContext()
  const { companyId } = useCompanyContext()
  const intl = useIntl()
  const { localeComparator } = useIntlSort()
  const { data: hasProviderTypes, isLoading: isLoadingHasProviderTypes } = useGetHasProviderTypeInCountry(
    companyCountryCode,
    companyId
  )
  const availableParentIds = getAvailableParentIdsByBenefitConfig(benefit)
  const history = useHistory()
  const params = new URLSearchParams(history.location.search)
  const providerTypeFromParams = params.get(ProviderFinderParam.ProviderType)
    ? params.get(ProviderFinderParam.ProviderType).split(",")
    : availableParentIds

  const filterByProviderType = (value): void => {
    const newSearchURL = getProviderFinderSearchURL({
      searchLocation: params.get(ProviderFinderParam.Search),
      radiusInKm: params.get(ProviderFinderParam.RadiusInKm),
      companyCountryCode,
      locationDetails: {
        latitude: Number(params.get(ProviderFinderParam.Latitude)),
        longitude: Number(params.get(ProviderFinderParam.Longitude)),
        countryCode: params.get(ProviderFinderParam.Country),
        isBroadArea: false
      },
      carrotPartner: params.get(ProviderFinderParam.CarrotPartner) === "true",
      providerType: value.length ? value.sort() : intl.formatMessage({ defaultMessage: "NONE" })
    })

    history.push({
      pathname: newSearchURL.pathname,
      search: newSearchURL.search
    })
  }

  const options = availableParentIds
    .filter((parentId) => {
      const providerType = PARENT_ID_TO_PROVIDER_COUNT_MAPPING[parentId]
      return hasProviderTypes && hasProviderTypes[providerType]
    })
    .sort((parentId1, parentId2) => {
      const parentFullDisplayName1 = intl.formatMessage(PARENT_ID_MAPPING[parentId1].fullDisplayName)
      const parentFullDisplayName2 = intl.formatMessage(PARENT_ID_MAPPING[parentId2].fullDisplayName)
      return localeComparator(parentFullDisplayName1, parentFullDisplayName2)
    })
    .map((parentId) => {
      const { fullDisplayName, getSecondaryText } = PARENT_ID_MAPPING[parentId]
      return (
        <Checkbox
          key={parentId}
          value={parentId}
          secondaryText={getSecondaryText && intl.formatMessage(getSecondaryText({ countryCode: companyCountryCode }))}
        >
          {intl.formatMessage(fullDisplayName)}
        </Checkbox>
      )
    })

  if (isLoadingHasProviderTypes) {
    return null
  }
  return (
    <FlexContainer direction="column" background="white" maxWidth="296px" borderRadius="6px" padding="12px">
      <CheckboxGroup
        name="providerType"
        value={providerTypeFromParams}
        onChange={filterByProviderType}
        aria-label={intl.formatMessage({ defaultMessage: "Select provider type" })}
      >
        <Stack spacing={sizing.spacing.small}>{options}</Stack>
      </CheckboxGroup>
    </FlexContainer>
  )
}
